/**
 * `visitorStore` — the widget's tiny, framework-free state container
 * (ARCHITECTURE §7). Holds the visitor session, its resume token, and the
 * message list. Deliberately dependency-free (no zustand) to keep the widget
 * bundle small; `useVisitorSession` binds it into React via `useSyncExternalStore`.
 *
 * The resume token is persisted to `localStorage` so a returning visitor rejoins
 * the same `ChatSession` (ARCHITECTURE §7 "resume via a signed session token").
 */

import type { ChatSession, Message } from '@flozic/shared/types';

/**
 * Storage key prefixes, namespaced per embed id.
 *
 * `LEGACY_NS` is the pre-rename prefix. It is READ-ONLY: a visitor mid-conversation holds
 * a resume token under the old key with a 7-day TTL, and renaming without a fallback would
 * silently sign them out — new session, lost history, a duplicate row for the agent. Reads
 * fall back to it and migrate the value forward on first touch (see {@link readNamespaced}).
 * Remove only after at least two full token TTL windows.
 */
const NS = 'lc:widget';
const LEGACY_NS = 'flozic:widget';

/**
 * Read a namespaced key, transparently migrating a pre-rename value forward.
 * Returns the current-key value when present; otherwise adopts the legacy value, rewrites
 * it under the new key, and clears the old one so the migration happens exactly once.
 */
function readNamespaced(store: Storage, cid: string, suffix: string): string | null {
  const key = `${NS}:${cid}:${suffix}`;
  const current = store.getItem(key);
  if (current !== null) return current;
  const legacy = store.getItem(`${LEGACY_NS}:${cid}:${suffix}`);
  if (legacy === null) return null;
  try {
    store.setItem(key, legacy);
    store.removeItem(`${LEGACY_NS}:${cid}:${suffix}`);
  } catch {
    // Storage full or blocked — the value still reads fine from `legacy` this session.
  }
  return legacy;
}

/** localStorage key for the resume token, namespaced per embed id. */
const tokenKey = (cid: string): string => `${NS}:${cid}:sessionToken`;

/**
 * localStorage key for the persisted session id, namespaced per embed id.
 * The public resume flow needs both the token (auth) and the session id (path),
 * so both are persisted together and cleared together.
 */
const sessionIdKey = (cid: string): string => `${NS}:${cid}:sessionId`;

/**
 * Storage key for "the visitor hid the floating launcher", namespaced per embed id.
 *
 * `sessionStorage`, NOT `localStorage` — deliberately the one key in this file that
 * isn't durable. A permanent dismissal would lock the visitor out of support on that
 * site with no way back (there is no other affordance to restore the launcher), so
 * the dismissal is scoped to the browsing session: it survives navigation around the
 * customer's site in this tab, and is gone in a new tab / after the tab is closed.
 */
const launcherDismissedKey = (cid: string): string => `${NS}:${cid}:launcherDismissed`;

/**
 * Whether this visitor dismissed the launcher in this browsing session.
 * A pure read — safe to call from a `useState` initializer, which is how the widget
 * avoids flashing the launcher before it can tell the loader to hide the frame.
 */
export function readLauncherDismissed(cid: string): boolean {
  try {
    return readNamespaced(sessionStorage, cid, 'launcherDismissed') === '1';
  } catch {
    // Private mode / storage blocked in a 3rd-party frame — the dismissal simply
    // won't outlive the page, which fails in the safe direction (launcher returns).
    return false;
  }
}

/** Persist (or clear) the launcher dismissal for this embed id. */
export function writeLauncherDismissed(cid: string, dismissed: boolean): void {
  try {
    if (dismissed) sessionStorage.setItem(launcherDismissedKey(cid), '1');
    else sessionStorage.removeItem(launcherDismissedKey(cid));
  } catch {
    // Storage disabled — in-memory state still drives the current page.
  }
}

/** High-level phase of the widget conversation flow. */
export type WidgetPhase =
  | 'loading' // fetching widget config
  | 'prechat' // showing the pre-chat form
  | 'chatting' // live session open
  | 'offline' // no agents online
  | 'ended' // session closed/left
  | 'error'; // config/session failure

/** Full widget state snapshot. */
export interface VisitorState {
  phase: WidgetPhase;
  /** Opaque signed resume token (localStorage-backed); null until session created. */
  sessionToken: string | null;
  session: ChatSession | null;
  messages: Message[];
  /** Whether the launcher panel is expanded. */
  open: boolean;
  /** The handling agent is typing (drives the three-dot bubble). */
  agentTyping: boolean;
  error: string | null;
}

const initialState: VisitorState = {
  phase: 'loading',
  sessionToken: null,
  session: null,
  messages: [],
  open: false,
  agentTyping: false,
  error: null,
};

type Listener = () => void;

/**
 * A minimal external store: `getSnapshot` + `subscribe` for React's
 * `useSyncExternalStore`, plus imperative mutators used by the session hook.
 */
class VisitorStore {
  private state: VisitorState = { ...initialState };
  private readonly listeners = new Set<Listener>();

  getSnapshot = (): VisitorState => this.state;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private set(patch: Partial<VisitorState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  // ---- mutators (called by useVisitorSession) ----

  setPhase(phase: WidgetPhase): void {
    this.set({ phase });
  }

  setError(error: string | null): void {
    this.set({ error, phase: error ? 'error' : this.state.phase });
  }

  setOpen(open: boolean): void {
    this.set({ open });
  }

  setSession(session: ChatSession | null): void {
    this.set({ session });
  }

  /** Agent typing indicator — self-clears after 6s in case the "stopped" frame never arrives
   *  (agent tab crash / dropped socket must not leave a permanent bubble). */
  private typingClearTimer: ReturnType<typeof setTimeout> | null = null;

  setAgentTyping(isTyping: boolean): void {
    if (this.typingClearTimer) {
      clearTimeout(this.typingClearTimer);
      this.typingClearTimer = null;
    }
    if (isTyping) {
      this.typingClearTimer = setTimeout(() => this.set({ agentTyping: false }), 6000);
    }
    if (this.state.agentTyping !== isTyping) this.set({ agentTyping: isTyping });
  }

  /**
   * Persist + set the resume token for this embed id. Clearing the token
   * (`null`) also drops the persisted session id so a stale resume is not
   * attempted after the session ends / the token is rejected.
   */
  setSessionToken(cid: string, token: string | null): void {
    try {
      if (token) {
        localStorage.setItem(tokenKey(cid), token);
      } else {
        localStorage.removeItem(tokenKey(cid));
        localStorage.removeItem(sessionIdKey(cid));
      }
    } catch {
      // Private mode / storage disabled — resume simply won't persist.
    }
    this.set({ sessionToken: token });
  }

  /** Read a persisted resume token (for reconnect on iframe reload). */
  readPersistedToken(cid: string): string | null {
    try {
      return readNamespaced(localStorage, cid, 'sessionToken');
    } catch {
      return null;
    }
  }

  /** Persist the session id alongside the token, for resume on reload. */
  persistSessionId(cid: string, sessionId: string): void {
    try {
      localStorage.setItem(sessionIdKey(cid), sessionId);
    } catch {
      // Private mode / storage disabled — resume simply won't persist.
    }
  }

  /** Read the persisted session id (paired with {@link readPersistedToken}). */
  readPersistedSessionId(cid: string): string | null {
    try {
      return readNamespaced(localStorage, cid, 'sessionId');
    } catch {
      return null;
    }
  }

  /** Replace the whole message list (e.g. on session resume/history load). */
  setMessages(messages: Message[]): void {
    this.set({ messages });
  }

  /**
   * Append/reconcile a message. If a message with the same `id` or matching
   * `clientMsgId` exists, it is replaced (optimistic-send reconciliation);
   * otherwise appended.
   */
  upsertMessage(message: Message): void {
    const list = this.state.messages;
    const idx = list.findIndex(
      (m) =>
        m.id === message.id ||
        (message.clientMsgId != null && m.clientMsgId === message.clientMsgId),
    );
    const next = idx >= 0 ? list.map((m, i) => (i === idx ? message : m)) : [...list, message];
    this.set({ messages: next });
  }

  /** Reset to the initial (post-config) state, keeping the phase caller-chosen. */
  reset(): void {
    this.state = { ...initialState };
    for (const l of this.listeners) l();
  }
}

/** Singleton store instance for the widget app. */
export const visitorStore = new VisitorStore();
